import { router } from "@inertiajs/react";
import { ArrowDown, LoaderIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AsyncSearchCombobox } from "@/components/admin-portal/patient/async-search-combobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import type { MislinkedOrder } from "@/types/admin-portal/order-ownership";
import type { UserSearchCandidate } from "@/types/admin-portal/patient";

export interface OrderChangeOwnerDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	order: MislinkedOrder;
}

// What the dialog actually needs to name an owner. Deliberately NOT UserSearchCandidate:
// that type carries `isAdmin`, and the suggestion below comes from the mislinked payload,
// which does not tell us whether the account is an admin. Claiming isAdmin: false there
// would be asserting something we do not know. A UserSearchCandidate from the picker
// satisfies this shape anyway. The real check is the controller's customer-only guard.
type OwnerChoice = {
	id: string;
	name: string;
	email: string;
};

// Go already tells us which account the order should belong to (the patient's own), so
// preselect it instead of making the admin search for what we already know. The picker
// stays available for when the suggestion is wrong. The payload carries the account's
// email but not its display name, so the email doubles as the label — which is what
// UsersController#search itself falls back to for accounts with no name.
function suggestedOwner(order: MislinkedOrder): OwnerChoice | null {
	if (!order.suggestedUserId) return null;

	return {
		id: order.suggestedUserId,
		name: order.suggestedEmail || order.patientName,
		email: order.suggestedEmail || "",
	};
}

export function OrderChangeOwnerDialog({
	open,
	onOpenChange,
	order,
}: OrderChangeOwnerDialogProps) {
	const { t } = useTranslation("order-ownership");
	const suggestion = suggestedOwner(order);
	const [selected, setSelected] = useState<OwnerChoice | null>(suggestion);
	const [reason, setReason] = useState("");
	const [loading, setLoading] = useState(false);

	const isSuggested = !!selected && selected.id === order.suggestedUserId;

	function reset() {
		setSelected(suggestion);
		setReason("");
	}

	async function onSubmit() {
		if (!selected) return;
		setLoading(true);
		try {
			const res = await fetch(
				`/admin-portal/order-ownership/${order.orderId}/owner`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						user_id: selected.id,
						reason: reason.trim() || undefined,
					}),
				},
			);
			const data = await res.json();
			if (res.ok && data.success) {
				toast.success(data.message || t("messages.success"));
				onOpenChange(false);
				reset();
				router.reload();
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			toast.error(t("messages.error"));
		} finally {
			setLoading(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) reset();
				onOpenChange(v);
			}}
		>
			<DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{t("change_owner.title")}</DialogTitle>
					<DialogDescription>{t("change_owner.description")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="rounded-lg border p-3">
						<p className="text-xs text-muted-foreground">
							{t("change_owner.order_label")}
						</p>
						<p className="font-medium">{order.registrationNumber}</p>
						<p className="text-sm text-muted-foreground">{order.patientName}</p>
					</div>

					<div className="rounded-lg border p-3">
						<p className="text-xs text-muted-foreground">
							{t("change_owner.current_owner_label")}
						</p>
						<p className="text-sm">
							{order.currentOwnerEmail || t("table.no_email")}
						</p>
					</div>

					<div className="flex justify-center">
						<ArrowDown className="size-4 text-muted-foreground" />
					</div>

					<div className="flex flex-col gap-2">
						<Label>{t("change_owner.new_owner_label")}</Label>
						{selected ? (
							<div className="flex items-center justify-between rounded-lg border p-3">
								<div className="flex flex-col gap-1">
									{isSuggested && (
										<Badge variant="secondary" className="w-fit">
											{t("change_owner.suggested_badge")}
										</Badge>
									)}
									<p className="font-medium">{selected.name}</p>
									{selected.email && selected.email !== selected.name && (
										<p className="text-sm text-muted-foreground">
											{selected.email}
										</p>
									)}
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setSelected(null)}
								>
									{t("change_owner.change_account")}
								</Button>
							</div>
						) : (
							<>
								<AsyncSearchCombobox<UserSearchCandidate>
									fetchUrl="/admin-portal/user-management/search"
									getKey={(u) => u.id}
									renderItem={(u) => (
										<div className="flex flex-col">
											<span>{u.name}</span>
											<span className="text-xs text-muted-foreground">
												{u.email}
											</span>
										</div>
									)}
									placeholder={t("change_owner.search_placeholder")}
									emptyText={t("change_owner.search_empty")}
									onSelect={setSelected}
								/>
								{suggestion && (
									<Button
										variant="ghost"
										size="sm"
										className="w-fit"
										onClick={() => setSelected(suggestion)}
									>
										{t("change_owner.use_suggested")}
									</Button>
								)}
							</>
						)}
						<p className="text-xs text-muted-foreground">
							{isSuggested
								? t("change_owner.suggested_hint")
								: t("change_owner.customer_only_hint")}
						</p>
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor="order-owner-reason">
							{t("change_owner.reason_label")}
						</Label>
						<Textarea
							id="order-owner-reason"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder={t("change_owner.reason_placeholder")}
							rows={3}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onSubmit} disabled={!selected || loading}>
						{loading && <LoaderIcon className="size-4 animate-spin" />}
						{t("change_owner.submit")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
